import { MagnifyingGlass, MapPin } from "@phosphor-icons/react";
import { useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { tariffDataSource } from "../data/staticDataSource";
import { track } from "../lib/analytics";
import { tariffAuthorityLabel } from "../lib/provider";
import type { MuniSummary } from "../types/tariffs";

interface MunicipalitySearchProps {
  autoFocus?: boolean;
  label?: string;
  excludeCodes?: string[];
  onSelect?: (municipality: MuniSummary) => void;
}

export function MunicipalitySearch({ autoFocus = false, label = "Find your municipality", excludeCodes = [], onSelect }: MunicipalitySearchProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<MuniSummary[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const requestId = useRef(0);
  const listId = useId();
  const navigate = useNavigate();

  useEffect(() => {
    const currentRequest = ++requestId.current;
    if (!query.trim()) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const timer = window.setTimeout(() => {
      tariffDataSource.search(query).then((nextResults) => {
        if (currentRequest !== requestId.current) return;
        setResults(nextResults);
        setActiveIndex(nextResults.length ? 0 : -1);
        setLoading(false);
      }).catch(() => {
        if (currentRequest === requestId.current) setLoading(false);
      });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [query]);

  function select(municipality: MuniSummary) {
    if (excludeCodes.includes(municipality.code)) return;
    track("search_performed", { result_count: results.length, query_length: query.trim().length });
    track("municipality_selected", { municipality_code: municipality.code, source: onSelect ? "compare_search" : "global_search" });
    setQuery("");
    setResults([]);
    if (onSelect) onSelect(municipality);
    else navigate(`/m/${municipality.code}`);
  }

  return (
    <div className="search-wrap">
      <label htmlFor="municipality-search">{label}</label>
      <div className="search-input-wrap">
        <MagnifyingGlass size={22} aria-hidden="true" />
        <input
          id="municipality-search"
          type="search"
          value={query}
          autoFocus={autoFocus}
          placeholder="Try Cape Town or Gauteng"
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-controls={listId}
          aria-expanded={Boolean(query.trim())}
          aria-activedescendant={activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown") {
              event.preventDefault();
              setActiveIndex((index) => Math.min(index + 1, results.length - 1));
            } else if (event.key === "ArrowUp") {
              event.preventDefault();
              setActiveIndex((index) => Math.max(index - 1, 0));
            } else if (event.key === "Enter" && results[activeIndex]) {
              event.preventDefault();
              select(results[activeIndex]);
            } else if (event.key === "Escape") {
              setResults([]);
            }
          }}
        />
        {loading && <span className="search-loading">Searching</span>}
      </div>
      {query && !loading && (
        <div className="search-results" id={listId} role="listbox">
          {results.map((municipality, index) => (
            <button
              id={`${listId}-${index}`}
              key={municipality.code}
              type="button"
              role="option"
              aria-selected={activeIndex === index}
              className={activeIndex === index ? "search-result is-active" : "search-result"}
              disabled={excludeCodes.includes(municipality.code)}
              aria-disabled={excludeCodes.includes(municipality.code)}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => select(municipality)}
            >
              <MapPin size={20} aria-hidden="true" />
              <span><strong>{municipality.name}</strong><small>{municipality.province} | {tariffAuthorityLabel(municipality)}</small></span>
              <span className="result-code">{excludeCodes.includes(municipality.code) ? "Selected" : municipality.dataStatus === "verified" ? municipality.code : municipality.dataStatus === "incomplete" ? "Pending" : "No data"}</span>
            </button>
          ))}
          {results.length === 0 && <p className="search-empty">No matching municipality in the national boundary index.</p>}
        </div>
      )}
    </div>
  );
}
